import {
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from './IconButton';
import { IconButtonInverted } from './IconButtonInverted';
import { XMarkIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { useTheme } from '../providers/ThemeContext';

interface Props {
  title: string;
  children: ReactNode;
  open: boolean;
  setOpen: (open: boolean) => void;
  hideBorder?: boolean;
}

export const Drawer = ({
  title,
  children,
  open,
  setOpen,
  hideBorder
}: Props): ReactElement => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current) {
        if (ref.current.contains(event.target as Node)) return;

        const path = event.composedPath();

        const isWithinPortal = path.some(
          (el) =>
            el instanceof Element &&
            (!!ref.current?.contains(el) || el.id === 'headlessui-portal-root')
        );

        if (!isWithinPortal) {
          setOpen(false);
        }
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, setOpen]);

  const { inverted } = useTheme();

  const CloseButton = inverted ? IconButtonInverted : IconButton;

  return createPortal(
    <div
      data-open={open}
      ref={ref}
      className={classNames(
        'rounded-sm fixed top-0 right-0 h-dvh w-full sm:w-auto z-100 translate-x-full data-[open=true]:translate-x-0 transition-all overflow-hidden flex flex-col border',
        inverted
          ? 'border-black-t-800 bg-white text-black'
          : 'border-grey-t-800 bg-background-tertiary'
      )}
    >
      <div className={classNames(`p-5 justify-between space-x-4 flex items-center`, {
        'border-b': !hideBorder,
        'border-black-t-800': !hideBorder && inverted,
        'border-grey-t-800': !hideBorder && !inverted,
      })}>
        <h2 className="font-bold font-grotesque text-h2">{title}</h2>
        <CloseButton onClick={() => setOpen(false)} Icon={XMarkIcon} />
      </div>
      {open && children}
    </div>,
    document.body
  );
};
